import isEqual from "lodash.isequal";
import { useEffect } from "react";
import { z } from "zod";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
type Config = {
  setConfig: (config: ConfigSchema) => void;
  resetConfig: () => void;
  config: ConfigSchema | undefined;
};

const configSchema = z.object({
  tokenKey: z.string().catch("tokenKey"),
  root: z.string().catch(""), // root path - for example in case of localization "/en"
  configFields: z
    .object({
      giftCard: z
        .object({
          value: z.string().catch(""),
        })
        .catch({
          value: "",
        }),

      senderName: z
        .object({
          label: z.string().catch("senderName.label"),
          placeholder: z.string().catch("senderName.placeholder"),
          value: z.string().catch(""),
        })
        .catch({
          label: "senderName",
          placeholder: "senderName",
          value: "",
        }),
      recieverName: z
        .object({
          label: z.string().catch("recieverName.label"),
          placeholder: z.string().catch("recieverName.placeholder"),
          value: z.string().catch(""),
        })
        .catch({
          label: "recieverName",
          placeholder: "recieverName",
          value: "recieverName",
        }),
      recieverEmail: z
        .object({
          label: z.string().catch("Receiver email"),
          placeholder: z.string().catch("Email.."),
          value: z.string().catch(""),
          active: z.boolean().catch(false),
        })
        .catch({
          label: "recieverEmail",
          placeholder: "recieverEmail",
          value: "recieverEmail",
          active: false,
        }),
      message: z
        .object({
          label: z.string().catch("Optional message"),
          placeholder: z.string().catch("message.placeholder"),
          value: z.string().catch("message.value"),
        })
        .catch({ label: "message", placeholder: "message", value: "message" }),
      amount: z
        .object({
          label: z.string().catch("amount.label"),
          value: z.string().catch("amount.value"),
        })
        .catch({ label: "amount", value: "amount" }),
      schedule: z
        .object({
          labels: z.array(z.string()).catch(["schedule.labels"]),
          active: z.boolean().catch(false),
        })
        .catch({
          labels: ["Deliver now", "Schedule for later"],
          active: false,
        }),
      button: z.string().catch("button"),
    })
    .catch({
      giftCard: {
        value: "",
      },
      senderName: { label: "From", placeholder: "Your name...", value: "" },
      recieverName: { label: "To", placeholder: "Receiver name...", value: "" },
      recieverEmail: {
        label: "Receiver email",
        placeholder: "Email address...",
        value: "",
        active: false,
      },
      message: {
        label: "Optional message",
        placeholder: "Message...",
        value: "",
      },
      amount: { label: "", value: "" },
      schedule: {
        labels: ["Deliver now", "Schedule for later"],
        active: false,
      },
      button: "Purchase",
    }),

  addressFields: z
    .object({
      title: z.string().catch("Checkout: Billing information"),
      firstName: z
        .object({
          label: z.string().catch("firstName.label"),
          placeholder: z.string().catch("firstName.placeholder"),
          value: z.string().catch(""),
        })
        .catch({
          label: "firstName",
          placeholder: "firstName",
          value: "firstName",
        }),
      lastName: z
        .object({
          label: z.string().catch("Last name"),
          placeholder: z.string().catch("Last name.."),
          value: z.string().catch(""),
        })
        .catch({
          label: "Last name",
          placeholder: "Last name..",
          value: "",
        }),
      address1: z
        .object({
          label: z.string().catch("address1.label"),
          placeholder: z.string().catch("address1.placeholder"),
          value: z.string().catch("address1.value"),
        })
        .catch({
          label: "address1",
          placeholder: "address1",
          value: "address1",
        }),
      address2: z
        .object({
          label: z.string().catch("address2.label"),
          placeholder: z.string().catch("address2.placeholder"),
          value: z.string().catch("address2.value"),
        })
        .catch({
          label: "address2",
          placeholder: "address2",
          value: "address2",
        }),
      city: z
        .object({
          label: z.string().catch("city.label"),
          placeholder: z.string().catch("city.placeholder"),
          value: z.string().catch("city.value"),
        })
        .catch({ label: "city", placeholder: "city", value: "city" }),
      state: z
        .object({
          label: z.string().catch("state.label"),
          placeholder: z.string().catch("state.placeholder"),
          value: z.string().catch("state.value"),
        })
        .catch({ label: "state", placeholder: "state", value: "state" }),
      zipCode: z
        .object({
          label: z.string().catch("zipCode.label"),
          placeholder: z.string().catch("zipCode.placeholder"),
          value: z.string().catch("zipCode.value"),
        })
        .catch({ label: "zipCode", placeholder: "zipCode", value: "zipCode" }),
      email: z
        .object({
          label: z.string().catch("email.label"),
          placeholder: z.string().catch("email.placeholder"),
          value: z.string().catch("email.value"),
        })
        .catch({ label: "email", placeholder: "email", value: "email" }),
      country: z
        .object({
          label: z.string().catch("country.label"),
          placeholder: z.string().catch("country.placeholder"),
          value: z.string().catch("country.value"),
        })
        .catch({ label: "country", placeholder: "country", value: "country" }),
      phoneNumber: z
        .object({
          label: z.string().catch("phoneNumber.label"),
          placeholder: z.string().catch("phoneNumber.placeholder"),
          value: z.string().catch("phoneNumber.value"),
        })
        .catch({
          label: "phoneNumber",
          placeholder: "phoneNumber",
          value: "phoneNumber",
        }),
      button: z.string().catch("button"),
    })
    .catch({
      title: "Billing information",
      firstName: { label: "First name", placeholder: "First name", value: "" },
      lastName: { label: "Last name", placeholder: "Last name", value: "" },
      address1: { label: "Address", placeholder: "Address", value: "" },
      address2: {
        label: "Address 2",
        placeholder: "Address 2 (optional)",
        value: "",
      },
      city: { label: "City", placeholder: "City", value: "" },
      state: { label: "State", placeholder: "State", value: "" },
      zipCode: { label: "Zip", placeholder: "Zip", value: "" },
      email: { label: "Email", placeholder: "Email", value: "" },
      country: { label: "Country", placeholder: "Country", value: "" },
      phoneNumber: { label: "Phone", placeholder: "Phone", value: "" },
      button: "Go to payment",
    }),

  summary: z
    .object({
      message: z.string().catch("message"),
      errorMessage: z.string().catch("errorMessage"),
    })
    .catch({
      message: "Message",
      errorMessage: "Something went wrong, try again, or contact support",
    }),

  giftCardUrl: z.string(),

  centra: z
    .object({
      url: z.string(),
      country: z.string(),
    })
    .required(),

  options: z
    .object({
      noPlaceholders: z.boolean().optional(),
    })
    .partial()
    .optional(),
});

export type ConfigSchema = z.infer<typeof configSchema>;

const useConfigStore = create(
  persist<Config>(
    (set) => ({
      config: undefined,
      setConfig: (config) => set({ config }),
      resetConfig: () => set({ config: undefined }),
    }),
    {
      name: "config-storage",
      storage: createJSONStorage(() => sessionStorage),
    }
  )
);

const useConfig = () => {
  const { config, setConfig, resetConfig } = useConfigStore();

  return { config, setConfig, resetConfig };
};

const useInitConfig = (initialConfig: ConfigSchema) => {
  const { setConfig, config } = useConfig();
  const parsedConfig = configSchema.parse(initialConfig);

  useEffect(() => {
    if (!parsedConfig) throw new Error("Invalid config in giftcard app");
    if (isEqual(parsedConfig, config)) return;
    setConfig(parsedConfig);
  }, [parsedConfig, setConfig]);
};

export { useConfig, useInitConfig };
